import type { CatalogV2ListRow } from '@skupreme/api-contracts/core';
import { useRef } from 'react';
import { channelLabel } from '../lib/v2';

type SeenChannel = { label: string; divisions: Set<string> };

/** A sales channel connected to the division (or account), whether or not a row lists it yet. */
export type ConnectedChannel = {
  id: string;
  label: string;
  divisions: readonly string[];
};

/*
 * The channels connected, then those on the pages seen so far. Filtering by
 * one channel narrows the page to it, so the list of channels to pick from
 * must outlive the rows.
 */
export function useSeenChannels(
  rows: CatalogV2ListRow[],
  connected: readonly ConnectedChannel[] = [],
) {
  const seen = useRef(new Map<string, SeenChannel>());

  for (const channel of connected) {
    if (!seen.current.has(channel.id)) {
      seen.current.set(channel.id, {
        label: channel.label,
        divisions: new Set(channel.divisions),
      });
    }
  }

  for (const row of rows) {
    for (const channel of row.channels) {
      const entry = seen.current.get(channel.connection_id) ?? {
        label: channelLabel(channel),
        divisions: new Set<string>(),
      };
      for (const division of row.divisions ?? []) {
        entry.divisions.add(division.name);
      }
      seen.current.set(channel.connection_id, entry);
    }
  }

  return channelOptions(seen.current);
}

/**
 * One option per connection. Connections that share a name (each division's own "Amazon North America") read with
 * their division, else with a number, so no two options look the same.
 */
export function channelOptions(seen: ReadonlyMap<string, SeenChannel>) {
  const sameName = new Map<string, number>();
  for (const { label } of seen.values()) {
    sameName.set(label, (sameName.get(label) ?? 0) + 1);
  }

  const taken = new Map<string, number>();
  return [...seen].map(([value, { label, divisions }]) => {
    if ((sameName.get(label) ?? 0) < 2) return { value, label };
    const where = [...divisions].join(', ');
    const named = where ? `${label} · ${where}` : label;
    const count = (taken.get(named) ?? 0) + 1;
    taken.set(named, count);
    return {
      value,
      label: count > 1 || !where ? `${named} (${count})` : named,
    };
  });
}
